import { filterClasses } from "@swivl-mono/helpers";
import {
  TicketPriority,
  TicketStatus,
  orderedTicketPriorities,
  orderedTicketStatuses,
  ticketPriorityLabels,
  ticketStatusLabels
} from "@swivl-mono/types";
import { DropdownContainer, DropdownOption, DropdownOptionItem, DropdownOptionVariant, DropdownPosition } from "@swivl-mono/ui";
import React, { ReactNode, useState } from "react";
import { FaUserGroup } from "react-icons/fa6";
import { FiChevronDown } from "react-icons/fi";
import { IoFlagSharp, IoPerson } from "react-icons/io5";
import { MdLocationOn } from "react-icons/md";
import { RiProgress4Line } from "react-icons/ri";

import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";

const CHIP_CLASS =
  "group flex cursor-pointer items-center gap-1 rounded bg-grey-50 px-2 py-1 text-sm font-semibold transition outline-none hover:bg-grey-100 data-[state=open]:bg-grey-100 group-data-[state=open]:bg-grey-100";
const CHEVRON_CLASS = "h-4 w-4 text-grey-600 transition group-data-[state=open]:rotate-180";

interface FilterChipProps {
  icon: ReactNode;
  label: string;
  options: DropdownOptionItem[];
  selectedValues: string[];
  onSelect: (value: string) => void;
  /** Single-select chips (Priority) close the menu on selection. */
  closeOnSelect?: boolean;
}

function FilterChip({ icon, label, options, selectedValues, onSelect, closeOnSelect }: FilterChipProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const count = selectedValues.length;
  return (
    <DropdownContainer
      isOpen={open}
      onOpenChange={setOpen}
      position={DropdownPosition.LEFT}
      modal={false}
      classNames={{ wrapper: "group cursor-pointer", dropdown: "min-w-40" }}
      trigger={
        <button className={filterClasses(CHIP_CLASS, count > 0 ? "text-orange-500" : "text-black")} type="button">
          {icon}
          <span>
            {label}
            {count > 0 ? ` (${count})` : ""}
          </span>
          <FiChevronDown className={CHEVRON_CLASS} />
        </button>
      }
    >
      {options.map((option) => (
        <DropdownOption
          key={option.value}
          option={option}
          isOptionSelected={(value) => selectedValues.includes(value)}
          onSelect={(value) => {
            onSelect(value);
            if (closeOnSelect) setOpen(false);
          }}
        />
      ))}
    </DropdownContainer>
  );
}

interface TicketSearchFiltersProps {
  memberOptions: { id: number; name: string }[];
  groupOptions: { slug: string; name: string }[];
  locationOptions: { id: number; name: string }[];
  /** Whether any keyword/filter is active (controls the Clear button). */
  showClear: boolean;
  onClear: () => void;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** The Search All filter row: Assignee, Group, Status, Priority, Location + Clear. */
export function TicketSearchFilters({
  memberOptions,
  groupOptions,
  locationOptions,
  showClear,
  onClear
}: TicketSearchFiltersProps): React.JSX.Element {
  const searchQuery = useTicketingStore((s) => s.searchQuery);
  const setSearchQuery = useTicketingStore((s) => s.setSearchQuery);
  const refreshSearch = useTicketingStore((s) => s.refreshSearch);

  const assigneeIds = searchQuery.assigneeMemberIds ?? [];
  const groupSlugs = searchQuery.routingGroupSlugs ?? [];
  const statuses = searchQuery.statuses ?? [];
  const locationIds = searchQuery.locationIds ?? [];
  const priority = searchQuery.priority;

  const apply = (patch: Parameters<typeof setSearchQuery>[0]): void => {
    setSearchQuery({ ...patch, offset: 0 });
    void refreshSearch();
  };

  const checkboxItem = (label: string, value: string): DropdownOptionItem => ({ label, value, variant: DropdownOptionVariant.WITH_CHECKBOX });

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <FilterChip
          icon={<RiProgress4Line className="h-4 w-4" />}
          label="Status"
          options={orderedTicketStatuses.map((s) => checkboxItem(ticketStatusLabels[s], s))}
          selectedValues={statuses}
          onSelect={(value) => {
            const next = toggle(statuses, value as TicketStatus);
            apply({ statuses: next.length ? next : undefined });
          }}
        />
        <FilterChip
          icon={<IoFlagSharp className="h-4 w-4" />}
          label="Priority"
          closeOnSelect
          options={orderedTicketPriorities.map((p) => ({ label: ticketPriorityLabels[p], value: p }))}
          selectedValues={priority ? [priority] : []}
          onSelect={(value) => apply({ priority: priority === value ? undefined : (value as TicketPriority) })}
        />
        <FilterChip
          icon={<IoPerson className="h-4 w-4" />}
          label="Assignee"
          options={memberOptions.map((m) => checkboxItem(m.name, String(m.id)))}
          selectedValues={assigneeIds.map(String)}
          onSelect={(value) => {
            const next = toggle(assigneeIds, Number(value));
            apply({ assigneeMemberIds: next.length ? next : undefined });
          }}
        />
        <FilterChip
          icon={<FaUserGroup className="h-4 w-4" />}
          label="Group"
          options={groupOptions.map((g) => checkboxItem(g.name, g.slug))}
          selectedValues={groupSlugs}
          onSelect={(value) => {
            const next = toggle(groupSlugs, value);
            apply({ routingGroupSlugs: next.length ? next : undefined });
          }}
        />
        <FilterChip
          icon={<MdLocationOn className="h-4 w-4" />}
          label="Location"
          options={locationOptions.map((l) => checkboxItem(l.name, String(l.id)))}
          selectedValues={locationIds.map(String)}
          onSelect={(value) => {
            const next = toggle(locationIds, Number(value));
            apply({ locationIds: next.length ? next : undefined });
          }}
        />
      </div>

      {showClear && (
        <button type="button" onClick={onClear} className="body-sm-alt shrink-0 cursor-pointer text-black transition hover:text-orange-500">
          Clear
        </button>
      )}
    </div>
  );
}
